import { useState, useEffect } from "react";
import axios from "axios";
import ReactECharts from "echarts-for-react";
import * as echarts from "echarts";

import RotateDevice from "../../../components/RotateDevice/RotateDevice";
import "./Trend.css";
import Lf23Loader from "../../../components/Lf23Loader";

const Trend = ({ filters, loading: parentLoading }) => {
    const API_URL = process.env.REACT_APP_API_URL || "http://localhost:3001/api";

    const [ingressiUscite, setIngressiUscite] = useState([]);
    const [trendAttivi, setTrendAttivi] = useState([]);
    const [dataLoading, setDataLoading] = useState(false);
    const [filterLoading, setFilterLoading] = useState(true);
    const [kpi, setKpi] = useState({ ingressi: 0, uscite: 0, delta: 0 });

    const [chartViewMode, setChartViewMode] = useState("mese");
    const [attiviViewMode, setAttiviViewMode] = useState("mese");

    const [selectedChartPeriod, setSelectedChartPeriod] = useState(null);
    const [selectedAttiviPeriod, setSelectedAttiviPeriod] = useState(null);

    const [isFullscreenCombo, setIsFullscreenCombo] = useState(false);
    const [isFullscreenAttivi, setIsFullscreenAttivi] = useState(false);

    useEffect(() => {
        const handleEsc = (e) => {
            if (e.key === 'Escape') {
                setIsFullscreenCombo(false);
                setIsFullscreenAttivi(false);
            }
        };
        document.addEventListener('keydown', handleEsc);
        return () => document.removeEventListener('keydown', handleEsc);
    }, [isFullscreenCombo, isFullscreenAttivi]);

    useEffect(() => {
        setFilterLoading(true);
    }, [filters]);

    useEffect(() => {
        const fetchData = async () => {
            setDataLoading(true);
            setSelectedChartPeriod(null);
            setSelectedAttiviPeriod(null);
            try {
                const params = {
                    fromDate: filters.fromDate,
                    toDate: filters.toDate,
                    granularityChart: chartViewMode === "mese" ? "mensile" : chartViewMode === "trimestre" ? "trimestrale" : "annuale",
                    granularityAttivi: attiviViewMode === "mese" ? "mensile" : attiviViewMode === "trimestre" ? "trimestrale" : "annuale",
                    settore: filters.settore?.map(s => s.value).join(",") || "",
                    tipoContratto: filters.tipoContratto?.map(t => t.value).join(",") || "",
                    ccnl: filters.ccnl?.map(c => c.value).join(",") || "",
                    motivoCessazione: filters.motivoCessazione?.map(m => m.value).join(",") || ""
                };
                const res = await axios.get(`${API_URL}/hr-generale/trend`, { params });
                setIngressiUscite(res.data.ingressiUscite || []);
                setTrendAttivi(res.data.trendAttivi || []);
                setKpi(res.data.kpi || { ingressi: 0, uscite: 0, delta: 0 });
            } catch (error) {
                console.error("Errore nel caricamento dati Trend:", error);
            } finally {
                setDataLoading(false);
                setFilterLoading(false);
            }
        };
        fetchData();
    }, [API_URL, filters, chartViewMode, attiviViewMode]);

    const handleChartClick = (params) => {
        if (params.componentType === 'series') {
            const label = params.name;
            const entry = ingressiUscite.find(d => d.mese === label);
            setSelectedAttiviPeriod(null);
            if (selectedChartPeriod?.label === label) {
                setSelectedChartPeriod(null);
            } else {
                setSelectedChartPeriod({ label, periodo: entry?.periodo });
            }
        }
    };

    const handleAttiviClick = (params) => {
        if (params.componentType === 'series') {
            const label = params.name;
            const entry = trendAttivi.find(d => d.data === label);
            setSelectedChartPeriod(null);
            if (selectedAttiviPeriod?.label === label) {
                setSelectedAttiviPeriod(null);
            } else {
                setSelectedAttiviPeriod({ label, periodo: entry?.periodo });
            }
        }
    };

    const isInRange = (periodoRaw, selectedPeriodo, viewMode) => {
        if (!selectedPeriodo || !periodoRaw) return false;
        const start = new Date(selectedPeriodo);
        const check = new Date(periodoRaw);
        let end;
        if (viewMode === 'trimestre') {
            end = new Date(start.getFullYear(), start.getMonth() + 3, start.getDate());
        } else if (viewMode === 'anno') {
            end = new Date(start.getFullYear() + 1, start.getMonth(), start.getDate());
        } else {
            end = new Date(start.getFullYear(), start.getMonth() + 1, start.getDate());
        }
        return check >= start && check < end;
    };

    // grafico combo
    const ingressiUsciteOptions = {
        tooltip: {
            trigger: "axis",
            axisPointer: { type: "shadow" }
        },
        legend: {
            bottom: 0,
            data: ["Ingressi", "Uscite", "Delta"]
        },
        grid: {
            left: "3%",
            right: "4%",
            bottom: "12%",
            containLabel: true
        },
        xAxis: {
            type: "category",
            data: ingressiUscite.map(d => d.mese)
        },
        yAxis: {
            type: "value"
        },
        series: [
            {
                name: "Ingressi",
                barGap: 0,
                type: "bar",
                data: ingressiUscite.map(d => ({
                    value: d.ingressi,
                    itemStyle: {
                        opacity: selectedChartPeriod
                            ? (d.mese === selectedChartPeriod.label ? 1.0 : 0.3)
                            : selectedAttiviPeriod
                                ? (isInRange(d.periodo, selectedAttiviPeriod.periodo, attiviViewMode) ? 1.0 : 0.3)
                                : 1.0
                    }
                })),
                itemStyle: {
                    color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                        { offset: 0, color: "#34d399" },
                        { offset: 1, color: "#059669" }
                    ]),
                },
                label: {
                    show: true,
                    position: "top",
                    fontSize: 11,
                    textStyle: {
                        fontSize: 13,
                        fontWeight: 'bold'
                    }
                }
            },
            {
                name: "Uscite",
                type: "bar",
                data: ingressiUscite.map(d => ({
                    value: d.uscite,
                    itemStyle: {
                        opacity: selectedChartPeriod
                            ? (d.mese === selectedChartPeriod.label ? 1.0 : 0.3)
                            : selectedAttiviPeriod
                                ? (isInRange(d.periodo, selectedAttiviPeriod.periodo, attiviViewMode) ? 1.0 : 0.3)
                                : 1.0
                    }
                })),
                itemStyle: {
                    color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                        { offset: 0, color: "#f87171" },
                        { offset: 1, color: "#dc2626" }
                    ]),
                },
                label: {
                    show: true,
                    position: "top",
                    fontSize: 11,
                    textStyle: {
                        fontSize: 13,
                        fontWeight: 'bold'
                    }
                }
            },
            {
                name: "Delta",
                type: "line",
                data: ingressiUscite.map(d => ({
                    value: (d.ingressi || 0) - (d.uscite || 0),
                    itemStyle: {
                        opacity: selectedChartPeriod
                            ? (d.mese === selectedChartPeriod.label ? 1.0 : 0.3)
                            : selectedAttiviPeriod
                                ? (isInRange(d.periodo, selectedAttiviPeriod.periodo, attiviViewMode) ? 1.0 : 0.3)
                                : 1.0
                    }
                })),
                smooth: true,
                lineStyle: { width: 2, color: "#ca8a04" },
                itemStyle: { color: "#ca8a04" },
                symbol: "circle",
                symbolSize: 6,
                label: {
                    show: true,
                    position: 'bottom',
                    backgroundColor: '#EEEEEE',
                    borderRadius: 5,
                    padding: 4,
                    textStyle: {
                        fontSize: 13,
                        fontWeight: 'bold'
                    }
                }
            }
        ]
    };

    // grafico linea
    const trendAttiviOptions = {
        tooltip: {
            trigger: "axis"
        },
        grid: {
            left: "3%",
            right: "4%",
            bottom: "15%",
            containLabel: true
        },
        xAxis: {
            type: "category",
            data: trendAttivi.map(d => d.data),
            boundaryGap: false
        },
        yAxis: {
            type: "value",
            name: "Attivi"
        },
        series: [
            {
                name: "Attivi",
                type: "line",
                data: trendAttivi.map(d => ({
                    value: d.attivi,
                    itemStyle: {
                        opacity: selectedChartPeriod
                            ? (isInRange(d.periodo, selectedChartPeriod.periodo, chartViewMode) ? 1.0 : 0.3)
                            : selectedAttiviPeriod
                                ? (d.data === selectedAttiviPeriod.label ? 1.0 : 0.3)
                                : 1.0
                    }
                })),
                lineStyle: { width: 2, color: "#016FB7" },
                itemStyle: { color: "#016FB7" },
                label: {
                    show: true,
                    backgroundColor: '#EEEEEE',
                    padding: 4,
                    borderRadius: 5,
                    position: 'bottom',
                    textStyle: {
                        fontSize: 13,
                        fontWeight: 'bold'
                    }
                }
            }
        ]
    };

    const displayKpi = selectedChartPeriod
        ? (() => {
            const entry = ingressiUscite.find(d => d.mese === selectedChartPeriod.label);
            if (!entry) return kpi;
            const delta = (entry.ingressi || 0) - (entry.uscite || 0);
            return { ingressi: entry.ingressi || 0, uscite: entry.uscite || 0, delta };
        })()
        : selectedAttiviPeriod
            ? (() => {
                const matching = ingressiUscite.filter(d =>
                    isInRange(d.periodo, selectedAttiviPeriod.periodo, attiviViewMode)
                );
                if (matching.length === 0) return kpi;
                const ingressi = matching.reduce((sum, d) => sum + (d.ingressi || 0), 0);
                const uscite = matching.reduce((sum, d) => sum + (d.uscite || 0), 0);
                return { ingressi, uscite, delta: ingressi - uscite };
            })()
            : kpi;

    const isLoading = parentLoading || filterLoading;

    return (
        <div className="trend-generale-container">
            {isLoading ? (
                <Lf23Loader />
            ) : (
                <>
                    <div className={`trend-gen-section${isFullscreenCombo ? ' fullscreen-trend' : ''}`}>
                        <RotateDevice active={isFullscreenCombo} />
                        <div className="charts-title">
                            <p className="trend-gen-section-title">Andamento Ingressi e Uscite</p>
                            {/* Toggle vista */}
                            <div className="oper-hr-table-view-type-container">
                                <button
                                    type="button"
                                    className={`oper-hr-table-view-btn ${chartViewMode === 'mese' ? 'active' : ''}`}
                                    onClick={() => setChartViewMode('mese')}
                                    title="Vista Mensile"
                                >
                                    <span>M</span>
                                </button>
                                <button
                                    type="button"
                                    className={`oper-hr-table-view-btn ${chartViewMode === 'trimestre' ? 'active' : ''}`}
                                    onClick={() => setChartViewMode('trimestre')}
                                    title="Vista Trimestrale"
                                >
                                    <span>T</span>
                                </button>
                                <button
                                    type="button"
                                    className={`oper-hr-table-view-btn ${chartViewMode === 'anno' ? 'active' : ''}`}
                                    onClick={() => setChartViewMode('anno')}
                                    title="Vista Annuale"
                                >
                                    <span>A</span>
                                </button>

                                <button
                                    className="trend-fullscreen-btn"
                                    onClick={() => setIsFullscreenCombo(!isFullscreenCombo)}
                                    title={isFullscreenCombo ? 'Esci da tutto schermo' : 'Tutto schermo'}
                                >
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        {isFullscreenCombo ? (
                                            <>
                                                <polyline points="4 14 10 14 10 20"></polyline>
                                                <polyline points="20 10 14 10 14 4"></polyline>
                                                <line x1="14" y1="10" x2="21" y2="3"></line>
                                                <line x1="3" y1="21" x2="10" y2="14"></line>
                                            </>
                                        ) : (
                                            <>
                                                <polyline points="15 3 21 3 21 9"></polyline>
                                                <polyline points="9 21 3 21 3 15"></polyline>
                                                <line x1="21" y1="3" x2="14" y2="10"></line>
                                                <line x1="3" y1="21" x2="10" y2="14"></line>
                                            </>
                                        )}
                                    </svg>
                                </button>

                            </div>
                        </div>
                        <div className="trend-gen-top-row">
                            <div className="trend-gen-kpi-column">
                                <div className="oper-hr-kpi-card">
                                    <div className="oper-hr-kpi-icon ingressi">
                                        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                                            <circle cx="8.5" cy="7" r="4"></circle>
                                            <line x1="20" y1="8" x2="20" y2="14"></line>
                                            <line x1="23" y1="11" x2="17" y2="11"></line>
                                        </svg>
                                    </div>
                                    <div className="oper-hr-kpi-info">
                                        <span className="oper-hr-kpi-value">{displayKpi.ingressi}</span>
                                    </div>
                                </div>
                                <div className="oper-hr-kpi-card">
                                    <div className="oper-hr-kpi-icon uscite">
                                        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                                            <circle cx="8.5" cy="7" r="4"></circle>
                                            <line x1="18" y1="8" x2="23" y2="13"></line>
                                            <line x1="23" y1="8" x2="18" y2="13"></line>
                                        </svg>
                                    </div>
                                    <div className="oper-hr-kpi-info">
                                        <span className="oper-hr-kpi-value">{displayKpi.uscite}</span>
                                    </div>
                                </div>
                                <div className="oper-hr-kpi-card">
                                    <div className="oper-hr-kpi-icon delta">
                                        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                                            <circle cx="9" cy="7" r="4"></circle>
                                            <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                                            <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                                        </svg>
                                    </div>
                                    <div className="oper-hr-kpi-info">
                                        <span className="oper-hr-kpi-value">{displayKpi.delta >= 0 ? `+${displayKpi.delta}` : displayKpi.delta}</span>
                                    </div>
                                </div>
                            </div>
                            <div className="trend-gen-chart-area">
                                {ingressiUscite.length > 0 ? (
                                    <ReactECharts
                                        option={ingressiUsciteOptions}
                                        style={{ height: "350px", width: "100%" }}
                                        onEvents={{ click: handleChartClick }}
                                        notMerge={true}
                                    />
                                ) : (
                                    <div className="oper-hr-empty-state">Nessun dato disponibile</div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Sezione 2: Trend Attivi */}
                    <div className={`trend-gen-section${isFullscreenAttivi ? ' fullscreen-trend' : ''}`}>
                        <RotateDevice active={isFullscreenAttivi} />
                        <div className="charts-title">
                            <p className="trend-gen-section-title">Trend Attivi</p>
                            {/* Toggle vista */}
                            <div className="oper-hr-table-view-type-container">
                                <button
                                    type="button"
                                    className={`oper-hr-table-view-btn ${attiviViewMode === 'mese' ? 'active' : ''}`}
                                    onClick={() => setAttiviViewMode('mese')}
                                    title="Vista Mensile"
                                >
                                    <span>M</span>
                                </button>
                                <button
                                    type="button"
                                    className={`oper-hr-table-view-btn ${attiviViewMode === 'trimestre' ? 'active' : ''}`}
                                    onClick={() => setAttiviViewMode('trimestre')}
                                    title="Vista Trimestrale"
                                >
                                    <span>T</span>
                                </button>
                                <button
                                    type="button"
                                    className={`oper-hr-table-view-btn ${attiviViewMode === 'anno' ? 'active' : ''}`}
                                    onClick={() => setAttiviViewMode('anno')}
                                    title="Vista Annuale"
                                >
                                    <span>A</span>
                                </button>

                                <button
                                    className="trend-fullscreen-btn"
                                    onClick={() => setIsFullscreenAttivi(!isFullscreenAttivi)}
                                    title={isFullscreenAttivi ? 'Esci da tutto schermo' : 'Tutto schermo'}
                                >
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        {isFullscreenAttivi ? (
                                            <>
                                                <polyline points="4 14 10 14 10 20"></polyline>
                                                <polyline points="20 10 14 10 14 4"></polyline>
                                                <line x1="14" y1="10" x2="21" y2="3"></line>
                                                <line x1="3" y1="21" x2="10" y2="14"></line>
                                            </>
                                        ) : (
                                            <>
                                                <polyline points="15 3 21 3 21 9"></polyline>
                                                <polyline points="9 21 3 21 3 15"></polyline>
                                                <line x1="21" y1="3" x2="14" y2="10"></line>
                                                <line x1="3" y1="21" x2="10" y2="14"></line>
                                            </>
                                        )}
                                    </svg>
                                </button>

                            </div>
                        </div>
                        {trendAttivi.length > 0 ? (
                            <ReactECharts
                                option={trendAttiviOptions}
                                style={{ height: "400px", width: "100%" }}
                                onEvents={{ click: handleAttiviClick }}
                                notMerge={true}
                            />
                        ) : (
                            <div className="oper-hr-empty-state">Nessun dato disponibile</div>
                        )}
                    </div>
                </>
            )}
        </div>
    );
};

export default Trend;


